import { useCallback } from 'react';
import { logger } from '@/shared/utils/logger';

interface ApiHelperProps {
  accessToken: string | null;
  rawUserId: string | null;
  rawProviderId: string | null;
  rawBusinessOwnerId: string | null;
}

type WithAuthFunction = <T>(
  fetchFn: (token: string, id: string) => Promise<T>,
  idType?: 'user' | 'provider' | 'shop',
) => Promise<T | null>;

/**
 * Hook to provide helper function for authenticated API requests
 */
export const useApiHelper = ({
  accessToken,
  rawUserId,
  rawProviderId,
  rawBusinessOwnerId,
}: ApiHelperProps): { withAuth: WithAuthFunction } => {
  // Safe API request helper that handles auth and ID null checks
  const withAuth = useCallback(
    async <T>(
      fetchFn: (token: string, id: string) => Promise<T>,
      idType: 'user' | 'provider' | 'shop' = 'user',
    ): Promise<T | null> => {
      if (!accessToken) {
        logger.warn('No access token available for API request');
        return null;
      }

      let id: string | null;

      switch (idType) {
        case 'provider':
          id = rawProviderId;
          break;
        case 'shop':
          id = rawBusinessOwnerId;
          break;
        case 'user':
        default:
          id = rawUserId;
      }

      if (!id) {
        logger.warn(`No ${idType} ID available for API request`);
        return null;
      }

      try {
        return await fetchFn(accessToken, id);
      } catch (error) {
        logger.error('API request failed:', error);
        return null;
      }
    },
    [accessToken, rawUserId, rawProviderId, rawBusinessOwnerId],
  );

  return { withAuth };
};
